<template>
    <Teleport to="body">
        <div
            v-if="modal && modal.visible"
            class="quotation-feedback-modal"
            role="dialog"
            aria-modal="true"
            aria-live="assertive"
        >
            <div class="quotation-feedback-modal__wrapper">
                <div class="quotation-feedback-modal__inner">
                    <button
                        type="button"
                        class="quotation-feedback-modal__close"
                        aria-label="Close modal"
                        @click="$emit('close-modal')"
                    >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M1 1L7 7M7 7L13 13M7 7L13 1M7 7L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>

                    <div class="quotation-feedback-modal__header">
                        <span
                            v-if="modal.type === 'loading'"
                            class="quotation-feedback-loader"
                            aria-hidden="true"
                        ></span>
                        <div
                            v-else
                            class="quotation-feedback-loader-alert"
                            aria-hidden="true"
                        >
                            <div
                                class="quotation-feedback-alert-icon animate"
                                :class="modal.type === 'error' ? 'quotation-feedback-alert-icon--error' : 'quotation-feedback-alert-icon--success'"
                            >
                                <template v-if="modal.type === 'error'">
                                    <span class="quotation-feedback-alert-line quotation-feedback-alert-tip2 animateTip2"></span>
                                    <span class="quotation-feedback-alert-line quotation-feedback-alert-tip3 animateTip3"></span>
                                </template>
                                <template v-else>
                                    <span class="quotation-feedback-alert-line quotation-feedback-alert-tip animateTip"></span>
                                    <span class="quotation-feedback-alert-line quotation-feedback-alert-long animateLong"></span>
                                </template>
                                <div class="quotation-feedback-alert-placeholder"></div>
                                <div class="quotation-feedback-alert-fix"></div>
                            </div>
                        </div>

                        <h3
                            class="quotation-feedback-modal__title"
                            :class="{
                                'quotation-feedback-modal__title--success': modal.type === 'success',
                                'quotation-feedback-modal__title--error': modal.type === 'error',
                            }"
                        >
                            {{ modal.title }}
                        </h3>
                    </div>

                    <div class="quotation-feedback-modal__body">
                        <div class="quotation-feedback-modal__message" v-html="modal.message"></div>
                    </div>

                    <div v-if="modal.confirmText" class="quotation-feedback-modal__footer">
                        <button
                            type="button"
                            class="quotation-feedback-modal__confirm"
                            @click="$emit('close-modal')"
                        >
                            {{ modal.confirmText }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="toast && toast.visible"
            class="quotation-feedback-toast"
            :class="toast.type === 'success' ? 'quotation-feedback-toast--success' : 'quotation-feedback-toast--error'"
            role="alert"
            aria-live="polite"
        >
            <div class="quotation-feedback-toast__icon" aria-hidden="true">
                <svg
                    v-if="toast.type === 'success'"
                    width="40"
                    height="41"
                    viewBox="0 0 40 41"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <circle cx="20" cy="20.5" r="20" fill="#22AD5C"/>
                    <path d="M19.9998 9.17578C13.7373 9.17578 8.6748 14.2383 8.6748 20.5008C8.6748 26.7633 13.7373 31.8633 19.9998 31.8633C26.2623 31.8633 31.3623 26.7633 31.3623 20.5008C31.3623 14.2383 26.2623 9.17578 19.9998 9.17578ZM19.9998 30.1758C14.6748 30.1758 10.3623 25.8258 10.3623 20.5008C10.3623 15.1758 14.6748 10.8633 19.9998 10.8633C25.3248 10.8633 29.6748 15.2133 29.6748 20.5383C29.6748 25.8258 25.3248 30.1758 19.9998 30.1758Z" fill="white"/>
                    <path d="M23.2246 17.0135L18.7621 21.3635L16.7371 19.376C16.3996 19.0385 15.8746 19.076 15.5371 19.376C15.1996 19.7135 15.2371 20.2385 15.5371 20.576L17.9371 22.901C18.1621 23.126 18.4621 23.2385 18.7621 23.2385C19.0621 23.2385 19.3621 23.126 19.5871 22.901L24.4246 18.251C24.7621 17.9135 24.7621 17.3885 24.4246 17.051C24.0871 16.7135 23.5621 16.7135 23.2246 17.0135Z" fill="white"/>
                </svg>
                <svg
                    v-else
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <circle opacity="0.1" cx="20" cy="20" r="20" fill="#B20000"/>
                    <path d="M20.0001 17.5968V20.5968M12.5577 23.2976C11.8649 24.4976 12.7313 25.9968 14.1161 25.9968H25.8841C27.2681 25.9968 28.1345 24.4976 27.4425 23.2976L21.5593 13.0992C20.8665 11.8992 19.1337 11.8992 18.4409 13.0992L12.5577 23.2976ZM20.0001 22.9968H20.0057V23.0032H20.0001V22.9968Z" stroke="#B20000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>

            <div class="quotation-feedback-toast__content">
                <strong>{{ toast.title }}</strong>
                <span>{{ toast.message }}</span>
            </div>

            <button
                type="button"
                class="quotation-feedback-toast__close"
                aria-label="Close alert"
                @click="$emit('close-toast')"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M6.00003 18L18 6M6.00003 6L18 18" stroke="#777777" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>
        </div>
    </Teleport>
</template>

<script>
export default {
    name : 'QuotationFeedback',
    props : {
        modal : {
            type : Object,
            default : () => ({
                visible : false,
                type : 'loading',
                title : '',
                message : '',
                confirmText : ''
            })
        },
        toast : {
            type : Object,
            default : () => ({
                visible : false,
                type : 'success',
                title : '',
                message : ''
            })
        }
    },
    emits : ['close-modal', 'close-toast']
}
</script>
